import {
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalCloseButton,
    ModalBody,
    Button,
    Box,
    Text,
    Flex,
    Divider,
} from '@chakra-ui/react';
import { ReactComponent as InvitationIcon } from 'assets/notification-invitation-icon.svg';
import { ReactComponent as SignedIcon } from 'assets/signed-icon.svg';
import { ReactComponent as PendingIcon } from 'assets/pending-icon.svg';
import { ReactComponent as NotSignedIcon } from 'assets/not-signed-icon.svg';
import { ReactComponent as PersonIcon } from 'assets/person-icon.svg';
import { TransactionType, ThresholdUpdateParams } from '../../types/transactions';

interface OwnProps {
    isOpen: boolean;
    onClose: () => void;
    walletName: string;
    transaction: TransactionType;
}

interface SignerGroup {
    label: string;
    threshold: number;
    signers: { accountId: string; name: string }[];
}

const InvitationPendingModal = ({ isOpen, onClose, walletName, transaction }: OwnProps) => {
    const params = transaction.params as ThresholdUpdateParams;
    const signatures = transaction.signatures || [];

    const oldSigners = params.oldSigners || [];
    const newSigners = params.newSigners || [];
    const oldThreshold = params.oldThreshold ?? 1;
    const newThreshold = params.newThreshold ?? 1;

    const hasSigned = (accountId: string) =>
        signatures.some((s) => s.accountId === accountId);

    const groups: SignerGroup[] = [
        {
            label: 'Current Signers',
            threshold: oldThreshold,
            signers: oldSigners.map((s) => ({ accountId: s.accountId, name: s.name || '' })),
        },
        {
            label: 'New Configuration',
            threshold: newThreshold,
            signers: newSigners.map((s) => ({ accountId: s.accountId, name: s.name || '' })),
        },
    ];

    const totalRequired = oldThreshold + newThreshold;
    const totalSigned = signatures.length;

    return (
        <Modal isOpen={isOpen} onClose={onClose} isCentered>
            <ModalOverlay />
            <ModalContent maxWidth="520px">
                <ModalHeader textAlign="center" pt={8}>
                    <Text size="xl" variant="bodyBold">
                        Invitation Signed
                    </Text>
                    <ModalCloseButton />
                </ModalHeader>
                <ModalBody pb={8} px={8}>
                    <Flex direction="column" gap={6} alignItems="center">
                        <Flex
                            alignItems="center"
                            justifyContent="center"
                            w={16}
                            h={16}
                            borderRadius="full"
                            bg="blue.50"
                        >
                            <InvitationIcon width={32} height={32} />
                        </Flex>

                        <Box textAlign="center">
                            <Text color="blackAlpha.600" mb={1}>
                                You've signed your invitation to
                            </Text>
                            <Text size="lg" variant="bodyBold">
                                {walletName}
                            </Text>
                        </Box>

                        <Divider />

                        <Box w="full" bg="primary" p={4} borderRadius={8}>
                            <Flex alignItems="center" gap={2} mb={3}>
                                <PersonIcon />
                                <Text size="xs" color="blackAlpha.600">
                                    {totalSigned} signed out of {totalRequired} required
                                </Text>
                            </Flex>

                            <Flex direction="column" gap={4}>
                                {groups.map((group) => {
                                    const groupSigned = group.signers.filter((s) => hasSigned(s.accountId)).length;
                                    return (
                                        <Box key={group.label}>
                                            <Flex alignItems="center" gap={1} mb={2}>
                                                <Text size="xs" variant="bodyBold">
                                                    {group.label}
                                                </Text>
                                                <Text size="xs" color="blackAlpha.500">
                                                    ({groupSigned} of {group.threshold} required)
                                                </Text>
                                            </Flex>
                                            <Flex direction="column" gap={1.5} pl={2}>
                                                {group.signers.map((signer) => {
                                                    const signed = hasSigned(signer.accountId);
                                                    const signerLabel = signer.name
                                                        ? `${signer.name} (${signer.accountId})`
                                                        : signer.accountId;

                                                    return (
                                                        <Flex key={`${group.label}-${signer.accountId}`} alignItems="center" gap={1}>
                                                            {signed ? (
                                                                <>
                                                                    <SignedIcon style={{ flexShrink: 0 }} />
                                                                    <Text size="xs" color="blackAlpha.700" isTruncated>
                                                                        {signerLabel}
                                                                    </Text>
                                                                    <Text whiteSpace="nowrap" size="xs" color="blackAlpha.700">
                                                                        - Signed
                                                                    </Text>
                                                                </>
                                                            ) : transaction.status === 0 ? (
                                                                <>
                                                                    <PendingIcon style={{ flexShrink: 0 }} />
                                                                    <Text size="xs" color="blackAlpha.700" isTruncated>
                                                                        {signerLabel}
                                                                    </Text>
                                                                    <Text whiteSpace="nowrap" size="xs" color="blackAlpha.700">
                                                                        - Pending
                                                                    </Text>
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <NotSignedIcon style={{ flexShrink: 0 }} />
                                                                    <Text size="xs" color="blackAlpha.400" isTruncated>
                                                                        {signerLabel}
                                                                    </Text>
                                                                    <Text whiteSpace="nowrap" size="xs" color="blackAlpha.400">
                                                                        - Not signed
                                                                    </Text>
                                                                </>
                                                            )}
                                                        </Flex>
                                                    );
                                                })}
                                            </Flex>
                                        </Box>
                                    );
                                })}
                            </Flex>
                        </Box>

                        <Text fontSize="xs" color="blackAlpha.500" textAlign="center">
                            The wallet update will complete once all required signatures are collected. You'll be able to access the wallet after execution.
                        </Text>

                        <Button
                            variant="outline"
                            w="full"
                            h="48px"
                            onClick={onClose}
                        >
                            Close
                        </Button>
                    </Flex>
                </ModalBody>
            </ModalContent>
        </Modal>
    );
};

export default InvitationPendingModal;
